import type {
  RagAssistantMessage,
  RagImageItem,
  RagProcedure,
  RagProcedureStep,
  RagProcedureVideo,
  RagVideoItem,
} from "../types/ragChatTypes";
import type { RagChatQueryResponse } from "../services/ragChatApi";
import { resolveMediaPath } from "../services/ragChatApi";
import { ragId } from "../data/mockRagData";
import { videoDisplayName } from "./ragChatUtils";

function mapImages(
  items: Array<{ path: string; metadata?: { page?: string | number } }> | undefined
): RagImageItem[] {
  if (!items?.length) return [];
  return items.map((item) => ({
    path: resolveMediaPath(item.path ?? ""),
    metadata: item.metadata,
  }));
}

function mapProcedureVideo(v: RagProcedureVideo | null | undefined): RagProcedureVideo | null {
  if (!v?.path) return null;
  return {
    ...v,
    path: resolveMediaPath(v.path),
  };
}

function mapProcedureVideos(
  v: RagProcedureStep["video"]
): RagProcedureVideo[] {
  const arr = !v ? [] : Array.isArray(v) ? v : [v];
  const out: RagProcedureVideo[] = [];
  for (const item of arr) {
    const mapped = mapProcedureVideo(item);
    if (mapped) out.push(mapped);
  }
  return out;
}

function mapProcedureImage(
  img: RagProcedureStep["image"]
): RagProcedureStep["image"] {
  if (!img?.path) return img ?? null;
  return { ...img, path: resolveMediaPath(img.path) };
}

function mapProcedure(proc: RagProcedure | null | undefined): RagProcedure | null {
  if (!proc?.steps?.length) return null;
  return {
    ...proc,
    video_fallback: mapProcedureVideo(proc.video_fallback),
    steps: proc.steps.map(
      (s: RagProcedureStep): RagProcedureStep => ({
        ...s,
        video: mapProcedureVideos(s.video),
        image: mapProcedureImage(s.image),
      })
    ),
  };
}

function mapInlineImages(
  inline: RagChatQueryResponse["inlineImages"] | undefined
): Record<string, RagImageItem[]> {
  if (!inline) return {};
  const out: Record<string, RagImageItem[]> = {};
  for (const [page, list] of Object.entries(inline)) {
    out[page] = mapImages(list);
  }
  return out;
}

export function mapQueryResponseToAssistantMessage(
  data: RagChatQueryResponse
): RagAssistantMessage {
  return {
    id: ragId("assistant"),
    role: "assistant",
    answer: data.answer ?? "No answer returned.",
    images: mapImages(data.images),
    videos: (data.videos ?? []).map((v): RagVideoItem => {
      const rawPath = v.path ?? "";
      const fromMeta = String(v.metadata?.pdf_name ?? "").trim();
      return {
        path: resolveMediaPath(rawPath),
        name: fromMeta || videoDisplayName(rawPath),
      };
    }),
    sources: data.sources ?? [],
    options: data.options ?? [],
    inlineImages: mapInlineImages(data.inlineImages),
    pageTables: data.pageTables ?? {},
    pageAlerts: data.pageAlerts ?? {},
    relatedQuestions: data.relatedQuestions ?? [],
    alternativePdfs: data.alternativePdfs ?? [],
    responseType: data.responseType ?? "answer",
    queryType: null,
    primarySource: data.primarySource ?? null,
    alternativeSources: data.alternativeSources ?? [],
    aiEnhancement: data.aiEnhancement ?? null,
    refusalReason: data.refusalReason ?? null,
    procedure: mapProcedure(data.procedure),
  };
}

export function createChatErrorAssistantMessage(message: string): RagAssistantMessage {
  return {
    id: ragId("assistant"),
    role: "assistant",
    answer: message,
    images: [],
    videos: [],
    sources: [],
    options: [],
    inlineImages: {},
    pageTables: {},
    pageAlerts: {},
    relatedQuestions: [],
    alternativePdfs: [],
    responseType: "answer",
    queryType: null,
    primarySource: null,
    alternativeSources: [],
    aiEnhancement: null,
    procedure: null,
  };
}
